import { z } from 'zod';
import { scanStateSchema, uploadStateSchema } from './record.js';
/**
 * HR's own uploads (#188, ADR-009 §2): evidence for a direct record and PDF template sources.
 *
 * The SAME quarantine as a candidate's upload — the same allowlist, write-only single-blob SAS,
 * sniffing, sealing and hash — and the same commit (`store-file`, or the template path). What
 * differs is only who is asking and what the reservation is for. One schema per payload, consumed
 * by the Record dialog, the Forms page and the API.
 */
/** Evidence HR attaches to a record: what a candidate may send, at the portal's largest size. */
export const HR_EVIDENCE_UPLOAD_LIMITS = {
    acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'],
    maxSizeMb: 20,
};
/** A template source is a fillable PDF and nothing else, within the fill's 20 MB bound. */
export const TEMPLATE_SOURCE_UPLOAD_LIMITS = {
    acceptedTypes: ['application/pdf'],
    maxSizeMb: 20,
};
/**
 * WHAT THE BYTES WERE STAGED FOR, decided when the slot is reserved and never afterwards.
 *
 * Quarantine records who a file belongs to and whether it scanned clean, and until #233 that was
 * enough, because the only consumer bound the upload to the record it was being filed against and
 * the record's requirement decided the category. Recording a signed offer from an upload added a
 * second consumer that derives the record FROM the upload, so there was nothing left to bind:
 * a TB result staged for a health requirement could be handed to the offer path and filed into
 * the personnel store, outside the restricted account and on the wrong retention clock. The ADA
 * and I-9 separation is a legal boundary, so "HR would not do that" is not the control.
 *
 * Absent means a record written before this existed. Those are treated as evidence, never as an
 * offer letter — the offer path requires the value explicitly, so a legacy or unlabelled upload
 * cannot satisfy it.
 */
export const hrUploadPurposeSchema = z.enum(['record-evidence', 'signed-offer']);
export const hrUploadIntentInputSchema = z
    .object({
    contentType: z.string().min(1),
    byteLength: z.number().int().positive(),
    /** Attacker-controlled text. Stored as metadata, never used to build a path. */
    fileName: z.string().min(1).max(255),
    /**
     * Evidence only: the record this upload belongs to, as returned by an earlier intent, so every
     * file one record rests on shares one reservation. Omitted on the first upload; the server
     * mints it. Never an existing step's id — the intent refuses that.
     */
    recordId: z.string().uuid().optional(),
    /** What these bytes are for. Defaulted so every existing caller keeps its meaning. */
    purpose: hrUploadPurposeSchema.default('record-evidence'),
})
    .strict();
export const hrUploadIntentSchema = z
    .object({
    uploadId: z.string().uuid(),
    /** Evidence: the record to send with the direct record. Absent for a template source. */
    recordId: z.string().uuid().optional(),
    uploadUrl: z.string().url(),
    expiresAt: z.string().datetime(),
})
    .strict();
/**
 * Where an HR upload stands. The scan state is shown so HR knows when a record can be saved or a
 * template published — neither is possible before `clean`.
 */
export const hrUploadStatusSchema = z
    .object({
    uploadId: z.string().uuid(),
    state: uploadStateSchema,
    scan: scanStateSchema,
    fileName: z.string().min(1),
    byteLength: z.number().int().nonnegative(),
    /** Of the validated bytes, once accepted: what a template publish must name. */
    sha256: z.string().regex(/^sha256:[a-f0-9]{64}$/).nullable(),
})
    .strict();
//# sourceMappingURL=hr-upload.js.map